/**
 * Poll activity statuses for all activities.
 *
 * This hook polls the API to detect when activity statuses change. It monitors
 * all activities and updates their status when they change.
 *
 * The hook automatically stops polling when:
 * 1. Maximum polling duration is reached (customizable, default: 90 seconds)
 * 2. An error occurs
 * 3. The component unmounts
 * 4. Polling is disabled via options
 *
 * @param {Array} activities - Array of activity data to monitor
 * @param {Object} options - Polling options
 * @param {number} options.pollInterval - Interval between polls in milliseconds (default: 5000)
 * @param {number} options.timeout - Maximum polling duration in milliseconds (default: 90000)
 * @param {boolean} options.enabled - Whether polling is enabled (default: true)
 * @returns {Object} Object containing activityStatuses state, startPolling, and stopPolling functions
 */
import { useEffect, useRef, useCallback, useState } from "react";
import { services } from "@src/dependencies";

const POLL_INTERVAL = 5000; // Check every 5 seconds
const MAX_POLL_DURATION = 90000; // Stop after 90 seconds to prevent infinite polling

export const useActivityStatusPolling = (activities, options = {}) => {
  const { libraryService } = services;
  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);
  const isPollingRef = useRef(false);
  const [activityStatuses, setActivityStatuses] = useState([]);

  const {
    pollInterval = POLL_INTERVAL,
    timeout = MAX_POLL_DURATION,
    enabled = true,
  } = options;

  /**
   * Stops polling and cleans up intervals/timeouts.
   */
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    isPollingRef.current = false;
  }, []);

  /**
   * Checks the current activity statuses and updates activities that have changed.
   * Always polls all activities to detect status changes from any source.
   */
  const checkActivityStatuses = useCallback(async () => {
    // Prevent concurrent polling
    if (isPollingRef.current) {
      return;
    }

    // If no activities, nothing to poll
    if (!activities || activities.length === 0) {
      return;
    }

    try {
      isPollingRef.current = true;

      // Extract activity IDs to send to API - poll ALL activities
      const activityIds = activities.map((activity) => activity.id);

      // Fetch latest statuses from API for all activities
      const statusesResponse =
        await libraryService.getAllActivityStatuses(activityIds);

      // Handle paginated response structure
      const statuses = statusesResponse.items || statusesResponse;
      const statusesMap = new Map(
        statuses.map((status) => [status.id, status])
      );

      // Update activity statuses with new statuses
      const updatedStatuses = activities.map((activity) => {
        const latestStatus = statusesMap.get(activity.id);
        if (latestStatus) {
          return {
            ...activity,
            status: latestStatus.status,
            error: latestStatus.error,
          };
        }
        return activity;
      });

      // Update the activity statuses state
      setActivityStatuses(updatedStatuses);
    } catch (error) {
      console.error("[Library] Error checking activity statuses:", error);
      stopPolling();
    } finally {
      isPollingRef.current = false;
    }
  }, [activities, libraryService, stopPolling]);

  /**
   * Starts polling for activity status changes.
   * Sets up an interval to check status and a timeout to stop after max duration.
   */
  const startPolling = useCallback(() => {
    // Prevent multiple polling instances
    if (intervalRef.current) {
      return;
    }

    // Always start polling if there are activities (regardless of their status)
    if (!activities || activities.length === 0) {
      return;
    }

    intervalRef.current = setInterval(checkActivityStatuses, pollInterval);

    // Safety timeout: stop polling after max duration
    timeoutRef.current = setTimeout(() => {
      stopPolling();
    }, timeout);
  }, [checkActivityStatuses, pollInterval, stopPolling, activities, timeout]);

  useEffect(() => {
    if (enabled && activities?.length > 0) {
      startPolling();
    } else {
      stopPolling();
    }

    return () => {
      stopPolling();
    };
  }, [enabled, activities, startPolling, stopPolling]);

  return { activityStatuses, startPolling, stopPolling };
};